import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { getOrder } from "@/services/getOrder";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { updateOrder } from "@/services/updateOrder";
import { cn } from "@/lib/utils";
import {
  DialogHeader,
  DialogFooter,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  initiateStkPush,
  InitiateStkPushSchemaType,
} from "@/services/initiateMpesaStkPush";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { confirmMpesaPayment } from "@/services/comfirmMpesaPayment";
import { AxiosError } from "axios";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, Settings, FileText, Printer, ArrowLeft } from "lucide-react";
import {
  OrderStatusMapping,
  PaymentStatusMapping,
} from "../models/order.model";
import {
  CashPaymentSchemaType,
  payWithCashService,
} from "@/services/payWithCash";
import {
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import Timeline from "@/shared/components/timeline";
import OrderPrinterComponent from "@/features/printer/printer.component";
import { payWithPdqService, PdqPaymentSchemaType } from "@/services/payWithPdq";

export const StkPushPhoneSchema = z.object({
  phone: z.string().min(10),
});

function OrderDetailsPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [isMpesaPhonenumberDialogOpen, setIsMpesaPhonenumberDialogOpen] =
    useState(false);

  const [mpesaStkSuccessMessage, setMpesaStkSuccessMessage] = useState<{
    message: string;
    severity:
      | "error"
      | "default"
      | "warning"
      | "success"
      | "destructive"
      | "info";
    showLoading?: boolean;
  }>({
    message: "",
    severity: "default",
    showLoading: true,
  });

  const closeOpenMpesaPhonenumberDialog = (open: boolean) => {
    setIsMpesaPhonenumberDialogOpen(open);
  };

  const handleChange = (value) => {
    setSelectedStatus(value);
  };

  const {
    data: order,
    isPending: isGetOrderPending,
    error,
    refetch,
  } = useQuery({
    queryKey: ["order"],
    queryFn: async () => {
      console.log("use query get order");
      const data = await getOrder(id);
      return data;
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
    retry: (failureCount, _error) => {
      return failureCount < 2;
    },
  });

  const [selectedStatus, setSelectedStatus] = useState("pending");
  const [checkoutRequestId, setCheckoutRequestId] = useState("");

  const orderMutation = useMutation({
    mutationFn: (order: any) => updateOrder(order._id, order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Order updated successfully");
    },

    onError: (error) => {
      toast.error(error.message);
      console.error(error);
    },
  });

  const cashPaymentMutation = useMutation({
    mutationFn: (data: CashPaymentSchemaType) => payWithCashService(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Order updated successfully");
    },

    onError: (error: any) => {
      toast.error(
        error.response?.data?.data?.message || "Error processing cash payment"
      );
      console.error(error);
    },
  });

  const pdqPaymentMutation = useMutation({
    mutationFn: (data:PdqPaymentSchemaType) => payWithPdqService(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Order updated successfully");
    },

    onError: (error: any) => {
      toast.error(
        error.response?.data?.data?.message || "Error processing pdq payment"
      );
      console.error(error);
    },
  });

  const handlePayWithCash = () => {
    cashPaymentMutation.mutate({
      order: order._id,
      company: order.company._id,
    });
  };

    const handlePayWithPDQ = () => {
    pdqPaymentMutation.mutate({
      order: order._id,
      company: order.company._id,
    });
  };

  const handleUpdateOrderStatus = () => {
    orderMutation.mutate({ _id: order._id, status: selectedStatus });
  };

  const initiateMpesaStkPushMutation = useMutation({
    mutationFn: (stkPushData: InitiateStkPushSchemaType) =>
      initiateStkPush(stkPushData),
    onSuccess: (response) => {
      console.log("initiateMpesaStkPus response", response);
      setMpesaStkSuccessMessage({
        message:
          "Initiate M-Pesa successful, Please wait for customer to complete transaction",
        severity: "warning",
        showLoading: true,
      });
      toast.success("Initiate M-Pesa successful");
      queryClient.invalidateQueries({ queryKey: ["order"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      setIsMpesaPhonenumberDialogOpen(false);
      handleConfirmMpesaPayment(response.stkResponse.CheckoutRequestID);
      setCheckoutRequestId(response.stkResponse.CheckoutRequestID ?? "");
    },

    onError: (error) => {
      const axiosError = error as AxiosError;
      toast.error(error.message);
      console.error("initiateMpesaStkPush", axiosError.response.data);
    },
  });

  const {
    isPending: isInitiateMpesaStkPushPending,
    // isSuccess: isInitiateMpesaStkPushSuccess,
    data: initiateMpesaStkResponse,
  } = initiateMpesaStkPushMutation;

  const initiateStkPushForm = useForm<{ phone: string }>({
    resolver: zodResolver(StkPushPhoneSchema),
    defaultValues: {
      phone: "",
    },
  });

  const handleInitiateMpesaStkPushPayment = (values: { phone: string }) => {
    console.log('user ',user);
    
    const payload = {
      ...values,
      // amount: order?.totalCost,
      amount: 1,
      orderId: order?._id,
      companyId: order.company._id,
    };
    initiateMpesaStkPushMutation.mutate(payload);
  };

  const confirmMpesaPaymentMutation = useMutation({
    mutationFn: (checkoutRequestID: string) =>
      confirmMpesaPayment(checkoutRequestID),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["order"] });
      // queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Confirm M-Pesa payment successfull");
      setMpesaStkSuccessMessage({
        message: "Mpesa payment successful",
        severity: "success",
      });
      setTimeout(() => {
        setMpesaStkSuccessMessage({ message: "", severity: "default" });
      }, 5000);
      console.log("confirmMpesaPayment response", response);
    },

    onError: (error) => {
      const axiosError = error as AxiosError;
      if (
        axiosError.response.data["data"]["mpesaResponse"]["errorCode"] ===
        "500.001.1001"
      ) {
        console.log("");
      } else if (
        axiosError.response.data["data"]["mpesaResponse"]["ResultCode"] ===
        "1037"
      ) {
        toast.error(
          axiosError.response.data["data"]["mpesaResponse"]["ResultDesc"]
        );
        setMpesaStkSuccessMessage({
          message:
            axiosError.response.data["data"]["mpesaResponse"]["ResultDesc"],
          severity: "error",
        });
      } else if (
        axiosError.response.data["data"]["mpesaResponse"]["ResultCode"] ===
        "1032"
      ) {
        toast.error(
          axiosError.response.data["data"]["mpesaResponse"]["ResultDesc"]
        );
        setMpesaStkSuccessMessage({
          message:
            axiosError.response.data["data"]["mpesaResponse"]["ResultDesc"],
          severity: "error",
        });
      } else if (
        axiosError.response.data["data"]["mpesaResponse"]["ResultCode"] ===
        "2001"
      ) {
        toast.error(
          axiosError.response.data["data"]["mpesaResponse"]["ResultDesc"]
        );
        setMpesaStkSuccessMessage({
          message:
            axiosError.response.data["data"]["mpesaResponse"]["ResultDesc"],
          severity: "error",
        });
      } else {
        toast.error(error.message);
        setMpesaStkSuccessMessage({
          message: "An error occurred while processing your request",
          severity: "error",
        });
      }

      // "ResultCode": "1032",
      //       "ResultDesc": "Request cancelled by user"
      // ResultCode
      console.error("confirmMpesaPayment", axiosError);
    },
    retryDelay: () => {
      return 10000;
    },
    retry: (_failureCount, error) => {
      const axiosError = error as AxiosError;
      console.error(
        "confirmMpesaPayment retry",
        axiosError.response.data["data"]["mpesaResponse"]["errorCode"] ===
          "500.001.1001"
      );
      return (
        axiosError.response.data["data"]["mpesaResponse"]["errorCode"] ===
        "500.001.1001"
      );
    },
  });

  const handleConfirmMpesaPayment = (checkoutRequestId: string) => {
    confirmMpesaPaymentMutation.mutate(checkoutRequestId);
  };

  const { isSuccess: isConfirmMpesaPaymentSuccess } =
    confirmMpesaPaymentMutation;

  useEffect(() => {
    if (order) {
      setSelectedStatus(order.status);
    }
  }, [order]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  if (isGetOrderPending) return <div>Loading...</div>;
  if (error)
    return (
      <Alert className="mt-4" variant={"error"}>
        <AlertTitle className="flex items-end">
          <Info className="h-4 w-4 mr-2" /> Error
        </AlertTitle>
        <AlertDescription className="ml-6">{error.message}</AlertDescription>
      </Alert>
    );
  if (!order && !isGetOrderPending)
    return <div>Order not found: {error.message}</div>;
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {/* Back */}
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">Order Details</h1>
          </div>
          <p className="text-muted-foreground">
            View and manage order #{order?._id?.slice(-6)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Export
          </Button>
         
          <OrderPrinterComponent order={order} />
        </div>
      </div>

      {/* Status Badge */}
      {/* <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
        ${order?.status === 'completed' ? 'bg-green-100 text-green-800' : 
          order?.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 
          order?.status === 'pending' ? 'bg-yellow-100 text-yellow-500' :
          order?.status === 'cancelled' ? 'bg-red-100 text-red-800' : 
          'bg-yellow-100 text-yellow-800'}"
      >
        {OrderStatusMapping[order?.status]}
      </div> */}

      {/* Existing Cards with enhanced styling */}
      <Card className="shadow-sm">
        <CardContent className="pt-3">
          <div className="flex justify-between py-2">
            <div>
              <p>
                <span className="mr-3 text-sm">Order Date:</span>
                <span className="font-medium">
                  {/* {dayjs(order.createdAt).format("DD/MM/YYYY HH:mm")} */}
                  <div className="uppercase">
                    {new Date(order.createdAt).toLocaleString("en-GM", {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                      hour12: true,
                    })}
                  </div>
                </span>
              </p>
              <p>
                <span className="mr-3 text-sm">Order Status:</span>
                <span
                  className={`px-2 py-1  rounded-md ${
                    order.status === "completed"
                      ? "text-green-600 bg-green-100"
                      : order.status === "in_progress"
                      ? "text-primary "
                      : order.status === "pending"
                      ? "text-yellow-600 bg-yellow-100 "
                      : order.status === "cancelled"
                      ? "text-red-600 bg-red-100"
                      : "text-yellow-600 bg-yellow-100"
                  }`}
                >
                  {OrderStatusMapping[order.status]}
                </span>
              </p>
              {/* <p>
                <span className="mr-3 text-sm">Order Id:</span>
                <span className="font-medium">{order._id}</span>
              </p> */}
            </div>
            {/* <Button>Print Receipt</Button> */}
          </div>
          <h2 className="mt-6">Car</h2>
          <p>
            <span className="mr-3 text-sm">{order.plateNumber}</span>
          </p>
          <h2 className="mt-6 mb-2">Assigned to</h2>
          <div className="space-y-2">
            {/* Display single employee (backward compatibility) */}
            {order?.employee && (
              <div className="flex items-center gap-2">
                <span className="text-sm">{order.employee.name || order.employee.email}</span>
              </div>
            )}
            
            {/* Display multiple employees */}
            {order?.employees && order.employees.length > 0 && (
              <div className="space-y-1">
                <div className="flex flex-wrap gap-2">
                  {order.employees.map((employee, index) => (
                    <span
                      key={employee._id || index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent"
                    >
                      {employee.name || employee.email}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Show message if no employees assigned */}
            {(!order?.employee && (!order?.employees || order.employees.length === 0)) && (
              <span className="text-sm text-muted-foreground">No employees assigned</span>
            )}
          </div>
          <h2 className="mt-6">Service</h2>
          <p>
            <span className="mr-3 text-sm">
              {order.service?.name || order?.expressServiceName || "-"}
            </span>
            <span className="font-medium">
              KSH {order.service?.price || order?.expressServiceCost || "-"}
            </span>
          </p>
          {order.addOns?.length ? (
            <>
              <h2 className="mt-6">Add Ons</h2>
              <div className="border rounded-md my-3">
                <Table>
                  {/* <TableCaption>A list of your recent addons.</TableCaption> */}
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Name</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      {/* <TableHead>Method</TableHead>
                <TableHead className="text-right">Amount</TableHead> */}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {order.addOns?.map((addon) => (
                      <TableRow key={addon._id}>
                        <TableCell className="font-medium">
                          {addon?.name}
                        </TableCell>
                        <TableCell className="text-right">
                          {addon?.price}
                        </TableCell>
                        {/* <TableCell>{addon.paymentMethod}</TableCell>
                  <TableCell className="text-right">
                    {addon.totalAmount}
                  </TableCell> */}
                      </TableRow>
                    ))}
                  </TableBody>
                  {/* <TableFooter>
              <TableRow>
                <TableCell colSpan={3}>Total</TableCell>
                <TableCell className="text-right">$2,500.00</TableCell>
              </TableRow>
            </TableFooter> */}
                </Table>
              </div>
            </>
          ) : null}
          <div className="mt-6 w-full flex justify-end items-center">
            <span className="mr-3 text-sm">Total</span>
            <span className="font-medium text-right">
              KSH {order?.totalCost}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-3">
        <CardContent>
          <h2 className="mt-6">Update Status</h2>
        </CardContent>
        <CardFooter className="flex flex-col items-start">
          <div className="flex justify-between w-full">
            <Select
              // disabled={selectedStatus === "completed"}
              value={selectedStatus}
              onValueChange={handleChange}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Update Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Status</SelectLabel>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="hold">Hold</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <Button
              // disabled={selectedStatus === "completed"}
              onClick={() => handleUpdateOrderStatus()}
            >
              Update
            </Button>
          </div>
          <Timeline
            className="mt-4"
            timeLineItems={order?.statusHistory.map((status) => ({
              title: OrderStatusMapping[status.newStatus],
              time: status.changedAt,
              subTitle: status?.updatedBy?.email,
              textColor: "string;",
              iconColor: "string",
              borderColor: "string",
              icon: status.newStatus === "pending" ? "CircleDot" : "Timer",
            }))}
          />
        </CardFooter>
      </Card>
      <Card className="mt-3">
        <CardContent>
          {mpesaStkSuccessMessage.message.length ? (
            <Alert className="mt-4" variant={mpesaStkSuccessMessage.severity}>
              <AlertTitle className="flex items-end">
                {mpesaStkSuccessMessage.showLoading ? (
                  <Settings
                    className={cn({
                      "animate-spin mr-2": true,
                      // [`text-${mpesaStkSuccessMessage.severity}`]: true,
                    })}
                  />
                ) : (
                  <Info className="h-4 w-4 mr-2" />
                )}
                Payment Status
              </AlertTitle>
              <AlertDescription className="ml-6">
                {mpesaStkSuccessMessage.message}
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="flex md:justify-between md:items-center flex-col md:flex-row">
            <div className="mb-2">
              <h2 className="mt-6">Payment Status{order.transaction?.paymentMethod && <span className="ml-2">({order.transaction?.paymentMethod})</span>}</h2>
              <h2
                className={cn({
                  "mt-2 font-semibold capitalize": true,
                  "text-success": order.paymentStatus === "paid",
                  "text-error": order.paymentStatus !== "paid",
                })}
              >
                {PaymentStatusMapping[order.paymentStatus]}
              </h2>
            </div>
            <div className="flex md:items-center md:justify-center">
              <AlertDialog>
                <AlertDialogTrigger asChild className="mr-4">
                  <Button
                    variant="outline"
                    className=""
                    disabled={order.paymentStatus === "paid"}
                  >
                    Pay With Cash
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently
                      process a transaction for this order.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handlePayWithCash()}>
                      Continue
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              {/* <Button
                disabled={order.paymentStatus === "paid"}
                onClick={() => {
                  setMpesaStkSuccessMessage({
                    message: "",
                    severity: "default",
                  });
                  closeOpenMpesaPhonenumberDialog(true);
                }}
                className="mr-4"
                variant="outline"
              >
                M-Pesa
              </Button> */}
                 <AlertDialog>
                <AlertDialogTrigger asChild className="mr-4">
                  <Button
                    variant="outline"
                    className=""
                    disabled={order.paymentStatus === "paid"}
                  >
                    Pay With PDQ
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently
                      process a transaction for this order.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handlePayWithPDQ()}>
                      Continue
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              {checkoutRequestId.length > 1 &&
              order.paymentStatus !== "paid" ? (
                <Button
                  disabled={order.paymentStatus === "paid"}
                  onClick={() => {
                    handleConfirmMpesaPayment(checkoutRequestId);
                  }}
                  variant="outline"
                >
                  Refresh
                </Button>
              ) : null}
              {/* </DialogTrigger> */}
              <Dialog open={isMpesaPhonenumberDialogOpen}>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>M-Pesa Payment</DialogTitle>
                    <DialogDescription>
                      Enter M-Pesa phone number
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="">
                      <Form {...initiateStkPushForm}>
                        <form
                          onSubmit={initiateStkPushForm.handleSubmit(
                            handleInitiateMpesaStkPushPayment
                          )}
                        >
                          <div className="w-full">
                            <FormField
                              control={initiateStkPushForm.control}
                              name="phone"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel> M-Pesa phone number</FormLabel>
                                  <FormControl>
                                    <Input
                                      className="col-span-3"
                                      placeholder="254XX..."
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <DialogFooter className="mt-4">
                            <DialogClose asChild>
                              <Button
                                variant="secondary"
                                onClick={() =>
                                  closeOpenMpesaPhonenumberDialog(false)
                                }
                              >
                                Cancel
                              </Button>
                            </DialogClose>
                            <Button
                              type="submit"
                              disabled={isInitiateMpesaStkPushPending}
                            >
                              {!isInitiateMpesaStkPushPending
                                ? "Continue"
                                : "Processing..."}
                            </Button>
                          </DialogFooter>
                        </form>
                      </Form>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default OrderDetailsPage;
